/* NFC Solutions — Session 31.4 — LAN workspace bootstrap
 * -----------------------------------------------------------------
 * Lets a brand-new device (never signed in, no internet) copy the
 * business workspace from the Coordinator over plain Wi-Fi and sign in
 * with a user PIN, and lets every device find out whether a Coordinator
 * already exists on the LAN when the cloud is unreachable.
 *
 * Transport: the native LAN HTTP server (port 8765) that already relays
 * WebRTC signalling now also serves JSON documents:
 *   • /doc/info       → { business_code, device_id, device_label, role,
 *                          coordinator: true, published_at }
 *   • /doc/bootstrap  → the same shape as GET /api/sync/snapshot
 *                        ({ business, collections, server_time, … })
 * Both are published by the Coordinator from its own IndexedDB.
 *
 * Fetching uses the native HTTP client (Capacitor `NfcsLan.fetchDoc` /
 * Tauri `lan_doc_fetch`) — the WebView origin is https so a plain
 * `fetch("http://192.168…")` would be blocked as mixed content. Regular
 * browser tabs are online-only and get graceful no-ops here.
 */

import { readAll, getBusiness, STORE_NAMES } from "./localDb";
import { listCachedLanPeers, recordLanPeer } from "./lanPeerCache";
import { getDeviceId } from "./device";

export const LAN_PORT = 8765;
const MANUAL_HOST_KEY = "nfcs:lan:manual_host";
const LAST_GOOD_HOST_KEY = "nfcs:lan:last_bootstrap_host";

// ─── native bridges ────────────────────────────────────────────────
async function _capLan() {
  try {
    const cap = typeof window !== "undefined" ? window.Capacitor : null;
    if (!cap || typeof cap.isNativePlatform !== "function" || !cap.isNativePlatform()) return null;
    return (cap.Plugins || {}).NfcsLan || null;
  } catch (_) { return null; }
}

let _tauriInvoke = null;
async function _tauri() {
  if (_tauriInvoke !== null) return _tauriInvoke || null;
  try {
    const w = typeof window !== "undefined" ? window : null;
    if (!w || (!w.__TAURI_INTERNALS__ && !w.__TAURI__ && !w.isTauri)) { _tauriInvoke = false; return null; }
    const mod = await import("@tauri-apps/api/core").catch(() => null);
    _tauriInvoke = mod && typeof mod.invoke === "function" ? mod.invoke : false;
    return _tauriInvoke || null;
  } catch (_) { _tauriInvoke = false; return null; }
}

/** True when this runtime can talk to the LAN document store at all. */
export async function lanBootstrapAvailable() {
  return !!((await _capLan()) || (await _tauri()));
}

// ─── manual coordinator address ────────────────────────────────────
export function getManualLanHost() {
  try { return (window.localStorage.getItem(MANUAL_HOST_KEY) || "").trim() || null; } catch (_) { return null; }
}
export function setManualLanHost(host) {
  try {
    const clean = String(host || "").trim().replace(/^https?:\/\//, "").replace(/\/.*$/, "");
    if (clean) window.localStorage.setItem(MANUAL_HOST_KEY, clean);
    else window.localStorage.removeItem(MANUAL_HOST_KEY);
    return clean || null;
  } catch (_) { return null; }
}

function _splitHost(h) {
  const [host, port] = String(h).split(":");
  return { host, port: Number(port) || LAN_PORT };
}

// ─── publishing (coordinator side) ─────────────────────────────────
export async function publishLanDoc(key, value) {
  const json = value == null ? "" : (typeof value === "string" ? value : JSON.stringify(value));
  const cap = await _capLan();
  if (cap && typeof cap.setDoc === "function") {
    try { return await cap.setDoc({ key, json }); } catch (_) { return null; }
  }
  const invoke = await _tauri();
  if (invoke) {
    try { return await invoke("lan_doc_set", { key, json }); } catch (_) { return null; }
  }
  return null;
}

/**
 * Build a `/api/sync/snapshot`-shaped document from this device's
 * IndexedDB and publish it (plus a small `info` card) on the LAN.
 * Called when this device becomes Coordinator and refreshed periodically.
 */
export async function publishBootstrapFromLocalDb({ deviceLabel = null, role = "coordinator" } = {}) {
  if (!(await lanBootstrapAvailable())) return { published: false, reason: "not-native" };
  const business = await getBusiness();
  if (!business || !business.slug) return { published: false, reason: "no-local-business" };
  const collections = {};
  for (const name of STORE_NAMES) {
    if (name === "meta" || name === "business") continue;
    try {
      const rows = await readAll(name);
      if (Array.isArray(rows) && rows.length) collections[name] = rows;
    } catch (_) { /* skip store */ }
  }
  const now = new Date().toISOString();
  const snapshot = {
    business,
    collections,
    server_time: now,
    cursor: now,
    source: "lan-coordinator",
    published_by: getDeviceId(),
  };
  const info = {
    business_code: String(business.slug).toLowerCase(),
    business_name: business.name || null,
    device_id: getDeviceId(),
    device_label: deviceLabel || null,
    role,
    coordinator: true,
    published_at: now,
    users: Array.isArray(collections.users) ? collections.users.length : 0,
  };
  await publishLanDoc("info", info);
  const r = await publishLanDoc("bootstrap", snapshot);
  return { published: !!r, bytes: r?.bytes || 0, info };
}

/** Retract the published documents (hand-off / logout). */
export async function unpublishBootstrap() {
  await publishLanDoc("info", null);
  await publishLanDoc("bootstrap", null);
}

// ─── fetching (joining device side) ────────────────────────────────
async function _fetchDoc(host, port, key, timeoutMs = 2500) {
  const cap = await _capLan();
  if (cap && typeof cap.fetchDoc === "function") {
    const r = await cap.fetchDoc({ host, port, key, timeoutMs });
    if (!r || Number(r.status) < 200 || Number(r.status) >= 300 || !r.json) return null;
    return JSON.parse(r.json);
  }
  const invoke = await _tauri();
  if (invoke) {
    const r = await invoke("lan_doc_fetch", { host, port, key, timeoutMs });
    if (!r || Number(r.status) < 200 || Number(r.status) >= 300 || !r.json) return null;
    return JSON.parse(r.json);
  }
  return null;
}

/**
 * Candidate LAN hosts, most promising first: manual address, last host
 * that worked, mDNS peers seen right now, then every peer we ever paired
 * with (cached IPv4s survive restarts).
 */
export async function discoverLanHosts({ scanMs = 2000 } = {}) {
  const out = [];
  const seen = new Set();
  const push = (host, port, meta = {}) => {
    if (!host) return;
    const key = `${host}:${port || LAN_PORT}`;
    if (seen.has(key)) return;
    seen.add(key);
    out.push({ host, port: port || LAN_PORT, ...meta });
  };
  const manual = getManualLanHost();
  if (manual) { const { host, port } = _splitHost(manual); push(host, port, { source: "manual" }); }
  try {
    const last = window.localStorage.getItem(LAST_GOOD_HOST_KEY);
    if (last) { const { host, port } = _splitHost(last); push(host, port, { source: "last-good" }); }
  } catch (_) { /* noop */ }
  // Live mDNS — Android (capacitor-zeroconf) or Windows (mdns-sd via Tauri).
  try {
    const cap = await _capLan();
    if (cap) {
      const mod = await import("./mdnsDiscovery");
      const peers = await mod.scanOnce({ durationMs: scanMs });
      for (const p of peers || []) push(p.ipv4, p.port, { source: "mdns", device_id: p.device_id, role: p.role });
    } else {
      const invoke = await _tauri();
      if (invoke) {
        try { await invoke("lan_mdns_browse", {}); } catch (_) { /* already browsing */ }
        await new Promise((r) => setTimeout(r, Math.min(scanMs, 2500)));
        const peers = await invoke("lan_mdns_peers", {});
        for (const p of peers || []) push(p.ipv4, p.port, { source: "mdns", device_id: p.device_id, role: p.role });
      }
    }
  } catch (_) { /* discovery is best-effort */ }
  for (const p of listCachedLanPeers()) push(p.ipv4, p.port, { source: "cache", device_id: p.device_id, role: p.role });
  return out;
}

/**
 * Find a Coordinator on the LAN. Returns `{ host, port, info }` or null.
 * `businessCode` (optional) restricts the match to one business.
 */
export async function findLanCoordinator({ businessCode = null, scanMs = 2000, perHostTimeoutMs = 1800 } = {}) {
  if (!(await lanBootstrapAvailable())) return null;
  const hosts = await discoverLanHosts({ scanMs });
  const want = businessCode ? String(businessCode).trim().toLowerCase() : null;
  // Probe in parallel — a stale cached IP must not delay a live one.
  const probes = hosts.map(async (h) => {
    try {
      const info = await _fetchDoc(h.host, h.port, "info", perHostTimeoutMs);
      if (!info || !info.coordinator) return null;
      if (want && String(info.business_code || "").toLowerCase() !== want) return null;
      return { ...h, info };
    } catch (_) { return null; }
  });
  const results = (await Promise.all(probes)).filter(Boolean);
  results.sort((a, b) => (a.source === "manual" ? -1 : b.source === "manual" ? 1 : 0));
  return results[0] || null;
}

/**
 * Copy the workspace of `businessCode` from a LAN Coordinator.
 * Returns the snapshot (same shape as /api/sync/snapshot) or null.
 */
export async function fetchBootstrapFromLan({ businessCode, scanMs = 2500, onStatus } = {}) {
  const say = (m) => { try { onStatus && onStatus(m); } catch (_) { /* noop */ } };
  say("Looking for a coordinator on this Wi-Fi…");
  const coord = await findLanCoordinator({ businessCode, scanMs });
  if (!coord) return null;
  say(`Found ${coord.info.device_label || "coordinator"} — copying workspace…`);
  const snap = await _fetchDoc(coord.host, coord.port, "bootstrap", 15000);
  if (!snap || !snap.business) return null;
  try { window.localStorage.setItem(LAST_GOOD_HOST_KEY, `${coord.host}:${coord.port}`); } catch (_) { /* noop */ }
  try {
    recordLanPeer({ device_id: coord.info.device_id, device_label: coord.info.device_label, role: coord.info.role, ipv4: coord.host, port: coord.port, source: "bootstrap", business_id: coord.info.business_code });
  } catch (_) { /* noop */ }
  return { snapshot: snap, coordinator: coord };
}
